// Copyright 2023 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import {Modal} from "antd";
import {ExclamationCircleFilled} from "@ant-design/icons";
import i18next from "i18next";
import * as Conf from "../Conf";
import * as Setting from "../Setting";

const {confirm} = Modal;
const {fetch: originalFetch} = window;

const demoModeCallback = (res) => {
  res.json().then(data => {
    if (Setting.isResponseDenied(data)) {
      confirm({
        title: i18next.t("general:This is a read-only demo site!"),
        icon: <ExclamationCircleFilled />,
        content: i18next.t("general:Go to writable demo site?"),
        okText: i18next.t("general:OK"),
        cancelText: i18next.t("general:Cancel"),
        onOk() {
          Setting.openLink(`https://demo.casdoor.com${location.pathname}${location.search}?username=built-in/admin&password=123`);
        },
        onCancel() {},
      });
    }
  });
};

const requestFilters = [];
const responseFilters = [];

if (Conf.IsDemoMode) {
  responseFilters.push(demoModeCallback);
}

window.fetch = async(url, option = {}) => {
  requestFilters.forEach(filter => filter(url, option));

  return new Promise((resolve, reject) => {
    originalFetch(url, option)
      .then(async res => {
        // 对于 API 请求，检查响应类型
        if (url.includes("/api/")) {
          const contentType = res.headers.get("content-type") || "";

          // 如果返回的是 HTML 而不是 JSON，说明后端出错了
          if (contentType.includes("text/html")) {
            const text = await res.text();
            // eslint-disable-next-line no-console
            console.error(`API 请求返回了 HTML 错误页面: ${url}`);
            // eslint-disable-next-line no-console
            console.error(`响应内容: ${text.substring(0, 500)}`);

            // 创建一个新的 Response 对象，返回 JSON 格式的错误信息
            const errorResponse = new Response(
              JSON.stringify({
                status: "error",
                msg: "服务器内部错误：后端返回了 HTML 错误页面。请检查后端服务日志。",
                data: text.substring(0, 500),
              }),
              {
                status: res.status,
                statusText: res.statusText,
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            resolve(errorResponse);
            return;
          }

          // 检查 HTTP 状态码，如果不是 2xx，尝试读取错误信息
          if (!res.ok && !contentType.includes("application/json")) {
            const text = await res.text();
            const errorResponse = new Response(
              JSON.stringify({
                status: "error",
                msg: `HTTP ${res.status}: ${res.statusText}`,
                data: text.substring(0, 500),
              }),
              {
                status: res.status,
                statusText: res.statusText,
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            resolve(errorResponse);
            return;
          }
        }

        if (!url.startsWith("/api/get-organizations")) {
          responseFilters.forEach(filter => filter(res.clone()));
        }
        resolve(res);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(`请求失败: ${url}`, error);
        reject(error);
      });
  });
};
